import { useState, useEffect } from "react";
import { FaArrowAltCircleRight } from "react-icons/fa";
import { useAuth } from "../../../context/auth";
import Post from "../../../helpers/ajax/post";
import BalanceModal from "../../+Shared components/balance";
import BypassModal from "../../+Shared components/BypassModal";
import DuplicateRequestPopup from "../../+Shared components/DuplicateRequestPopup";
import RewinterizationModal from "../../+Shared components/RewinterizationModal";

const DewinterForm = () => {
    const { user, colonyData } = useAuth();
    // Multi-unit: dropdown of the user's units in the active colony
    const activeColony = (user.colonies || []).find((c) => c.colony_id === user.colony_id);
    const unitOptions = activeColony?.units?.length ? activeColony.units : [user.unit];
    const [SelectedUnitRaw, setSelectedUnit] = useState(unitOptions.length > 1 ? "" : (unitOptions[0] || user.unit));
    // Reset the selection if the active colony's units changed (colony switch)
    const SelectedUnit = unitOptions.includes(SelectedUnitRaw) ? SelectedUnitRaw : "";
    const [BypassCode, updateBypassCode] = useState("");
    const [recentDuplicateDate, setRecentDuplicateDate] = useState(null);
    const [SubmitSuccess, setSubmitSuccess] = useState(false);
    // "Don't do before" (optional start) and "Needs to be done by" (required due)
    const [startDate, setStartDate] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [toiletAmount, setToiletAmount] = useState(1);
    const [sinkAmount, setSinkAmount] = useState(1);
    const [washingMachineAmount, setWashingMachineAmount] = useState(1);
    const [thermostatAmount, setThermostatAmount] = useState(1);
    const [Combination, setCombination] = useState("");
    const [MasterBedroomCombination, setMasterBedroomCombination] = useState("");
    const [Door, setDoor] = useState("Main Door");
    const [SpecialInstructions, setSpecialInstructions] = useState("");
    // const [BreakerOn, setBreakerOn] = useState(false);
    const [HeatTemperature, setHeatTemperature] = useState(null);
    // const [AcceptedAgreement, setAcceptedAgreement] = useState(false);
    const [Error, setError] = useState(null);
    const [Requesting, setRequesting] = useState();
    const [showRewinterizationModal, setShowRewinterizationModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    // Winterization limit check is per unit: remember which unit/colony the
    // latest count result belongs to so the banner never shows stale data
    // from a previously selected unit or colony.
    const [limitInfo, setLimitInfo] = useState({ unit: "", colony_id: "", exceeded: false });
    useEffect(() => {
        if (!user.user_id || !SelectedUnit)
            return setLimitInfo({ unit: "", colony_id: "", exceeded: false });
        let cancelled = false;
        (async () => {
            try {
                const response = await Post("/api/user/getRequestCount", { type: 1, colony_id: user.colony_id, unit: SelectedUnit });
                if (cancelled) return;
                setLimitInfo({ unit: SelectedUnit, colony_id: user.colony_id, exceeded: response.count >= 2 });
            } catch (error) {
                if (!cancelled)
                    setLimitInfo({ unit: SelectedUnit, colony_id: user.colony_id, exceeded: false });
            }
        })();
        return () => { cancelled = true; };
    }, [user.user_id, user.colony_id, SelectedUnit]);
    // Only "limit exceeded" when the fetched count belongs to the currently
    // selected unit in the active colony.
    const rewinterizationLimitExceeded = limitInfo.exceeded
        && limitInfo.unit === SelectedUnit
        && limitInfo.colony_id === user.colony_id;

    const submitRequest = async (ev, ignoreDuplicates = false, force = false) => {
        ev?.preventDefault();
        setError(null);
        // if (!AcceptedAgreement) {
        //     return setError("Please accept the terms and conditions.");
        // }
        if (unitOptions.length > 1 && !SelectedUnit) {
            return setError("Please select a unit.");
        }
        if (rewinterizationLimitExceeded && !force)
            return setShowConfirmationModal(true);
        if (!dueDate)
            return setError("Please provide a \"Needs to be done by\" date.");
        if (startDate && dueDate && startDate > dueDate)
            return setError("The \"Don't do before\" date cannot be after the \"Needs to be done by\" date.");
        setRequesting(true);
        try {
            let response = (await Post("/api/user/request", { //-$$- rule 1
                colony_id: user.colony_id,
                unit: SelectedUnit,
                colonyName: colonyData.name,
                startDate: startDate,
                dueDate: dueDate,
                toiletAmount: toiletAmount,
                sinkAmount: sinkAmount,
                washingMachineAmount: washingMachineAmount,
                thermostatAmount: thermostatAmount,
                specialInstructions: SpecialInstructions,
                todo: "De-Winterize",
                combinationCode: Combination,
                masterBedroomCombination: MasterBedroomCombination,
                door: Door,
                // breaker: BreakerOn,
                bypassCode: BypassCode,
                ignore_duplicates: ignoreDuplicates,
                heatTemperature: HeatTemperature
            }));
            if (response?.recent_duplicate_date)
                return setRecentDuplicateDate(response.recent_duplicate_date);
            setSubmitSuccess(true);
            if (new Date() < (new Date((new Date()).getFullYear(), 3, 1)) && !rewinterizationLimitExceeded)
                setShowRewinterizationModal(true);
        } catch (error) {
            setError(error.error);
        } finally {
            setRequesting(false);
        }
    };

    // Today's date (YYYY-MM-DD) is the minimum for the start-date input;
    // tomorrow is the hard minimum for the due date.
    const todayDate = new Date(new Date().toLocaleDateString());
    const todayISO = todayDate.toISOString().split('T')[0];
    const tomorrowDate = new Date(todayDate);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowISO = tomorrowDate.toISOString().split('T')[0];

    return (
        <>
            <BalanceModal blockRequest={true} updateBypassCode={updateBypassCode} />
            {rewinterizationLimitExceeded
                && (<div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">Note that we will be unable to process additional <b>winterization</b> requests for this season as you have already reached the maximum allowed limit.</div>)}
            {recentDuplicateDate && <DuplicateRequestPopup recentDuplicateDate={recentDuplicateDate} close={() => {
                setRecentDuplicateDate(null);
                setRequesting(false);
            }} continueAnyway={() => {
                setRecentDuplicateDate(null);
                submitRequest(null, true, true);
            }} />}
            <form onSubmit={submitRequest}>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Full Name</div>
                    <div className="md:col-span-5 col-span-12">
                        <input disabled={true} name="full_name" value={`${user.first_name} ${user.last_name}`} type="text" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Unit #</div>
                    <div className="md:col-span-5 col-span-12">
                        {unitOptions.length > 1 ? (
                            <select
                                name="unit"
                                value={SelectedUnit}
                                onChange={(e) => setSelectedUnit(e.target.value)}
                                disabled={SubmitSuccess}
                                className="focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold"
                            >
                                <option value="" disabled>Select unit</option>
                                {unitOptions.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </select>
                        ) : (
                            <input disabled={true} name="unit" value={user.unit} type="text" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-start">
                        <div className="md:pt-2 font-semibold">Don't do before (Optional)</div>
                    </div>
                    <div className="md:col-span-5 col-span-12">
                        <input disabled={SubmitSuccess} name="startDate" onChange={(e) => {
                            const value = e.target.value;
                            setStartDate(value);
                            // Keep the due date on/after the start date: if the newly
                            // picked start is later than the chosen due date, push the
                            // due date up so it always matches the start date.
                            if (value && dueDate && value > dueDate) setDueDate(value);
                        }} value={startDate} min={todayISO} type="date" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-start">
                        <div className="md:pt-2 font-semibold">Needs to be done by</div>
                    </div>
                    <div className="md:col-span-5 col-span-12">
                        <input required disabled={SubmitSuccess} name="dueDate" onChange={(e) => setDueDate(e.target.value)} value={dueDate} min={(startDate && startDate > tomorrowISO) ? startDate : tomorrowISO} type="date" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                        {dueDate && Math.floor((new Date(`${dueDate.split('-')[1]}/${dueDate.split('-')[2]}/${dueDate.split('-')[0]}`).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24)) < 3 && (
                            <p className="text-red-700 mt-1">We cannot guarantee timely service for dates within 3 days.</p>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Combination Code</div>
                    <div className="md:col-span-5 col-span-12">
                        <input required disabled={SubmitSuccess} name="combination" onChange={(e) => setCombination(e.target.value)} value={Combination} type="text" placeholder="12345" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Master Bedroom Combination (Optional)</div>
                    <div className="md:col-span-5 col-span-12">
                        <input disabled={SubmitSuccess} name="masterBedroomCombination" onChange={(e) => setMasterBedroomCombination(e.target.value)} value={MasterBedroomCombination} type="text" placeholder="1, 2-3, 4, 5" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Which Door Does This Code Apply To?</div>
                    <div className="md:col-span-5 col-span-12">
                        <select required disabled={SubmitSuccess} name="door" onChange={(e) => setDoor(e.target.value)} value={Door} className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold">
                            <option value="Main Door">Main Door</option>
                            <option value="Right Side Door">Right Side Door</option>
                            <option value="Left Side Door">Left Side Door</option>
                            <option value="Back Door">Back Door</option>
                            <option value="Back Door">Basement Door</option>
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Number of Toilets</div>
                    <div className="md:col-span-5 col-span-12">
                        <select className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" value={toiletAmount}
                            onChange={(e) => setToiletAmount(e.target.value)}>
                            {[...Array(10)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Number of Sinks</div>
                    <div className="md:col-span-5 col-span-12">
                        <select className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" value={sinkAmount}
                            onChange={(e) => setSinkAmount(e.target.value)}>
                            {[...Array(15)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Number of Washing Machines</div>
                    <div className="md:col-span-5 col-span-12">
                        <select className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" value={washingMachineAmount}
                            onChange={(e) => setWashingMachineAmount(e.target.value)}>
                            {[...Array(3)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold">Number of Thermostats</div>
                    <div className="md:col-span-5 col-span-12">
                        <select className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full h-12 px-4 border focus:outline-none border-dependable-gold" value={thermostatAmount}
                            onChange={(e) => setThermostatAmount(e.target.value)}>
                            {[...Array(10)].map((_, i) => (
                                <option key={i + 1} value={i + 1}>{i + 1}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-start md:pt-2 font-semibold">Heat temperature</div>
                    <div className="md:col-span-5 col-span-12">
                        <input required type="number" disabled={SubmitSuccess} name="temperature" onChange={(e) => setHeatTemperature(e.target.value)} value={HeatTemperature} onWheel={e => e.currentTarget.blur()} placeholder="Heat temperature" className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full py-4 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-start md:pt-2 font-semibold">Special instructions</div>
                    <div className="md:col-span-5 col-span-12">
                        <textarea disabled={SubmitSuccess} name="combination" onChange={(e) => setSpecialInstructions(e.target.value)} value={SpecialInstructions} type="text" placeholder="Special instructions" rows={3} className="disabled:opacity-60 focus:ring-2 ring-dependable ring-offset-2 w-full py-4 px-4 border focus:outline-none border-dependable-gold" />
                    </div>
                </div>
                {/* <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-start md:pt-2 font-semibold">Should we turn on your electric breaker?</div>
                    <div className="md:col-span-5 col-span-12">
                        <div className="inline-block rounded-md overflow-hidden border border-dependable-gold">
                            <button type="button" onClick={() => setBreakerOn(true)} className={`focus:outline-none transition duration-200 py-2 px-10 font-semibold ${BreakerOn ? "bg-dependable-gold text-white" : ""}`}>
                                Yes
                            </button>
                            <button type="button" onClick={() => setBreakerOn(false)} className={`focus:outline-none transition duration-200 py-2 px-10 font-semibold ${!BreakerOn ? "bg-dependable-gold text-white" : ""}`}>
                                No
                            </button>
                        </div>
                        <p className="font-semibold mt-1">If you don't select a preference we will leave your breaker on.</p>
                    </div>
                </div> */}
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12"></div>
                    {/* <div className="md:col-span-8 col-span-12 py-4">
                        <p className="text-xl font-semibold">AGREEMENT FOR WINTERIZATION SERVICES:</p>
                        <p className="">AGREEMENT made by the "Owner":</p>
                        <p className="h-44 overflow-scroll mt-2 mb-6 shadow-inner text-sm p-2">
                            AGREED that, at the request of the unit Owner, the Condo shall perform winterization services for said Owner including, but not limited to, draining the water from the unit's pipes, shutting off the water entering the home, and filling certain pipes and receptacles with compatible chemicals to prevent frost and/or freeze damage to the water system within the Owner's Unit, and it is further AGREED that this Winterization service is being performed as a courtesy for the Owners who do not use their premises over the Winter months, which must be acknowledge in the same manner by Provider and that no unit will be done unless the procedure herein is followed, and it is further
                            <br />
                            <br />
                            AGREED that it is the responsibility of unit owners to indicate to management agency in time when they request winterization, of any changes they made to their unit that needs to be known when winterizing unit, and it is further AGREED that Owner has the right to arrange alternate Winterization service with their choice of provider at Owner's expense, and that Condo as indemnitee is held harmless in the event that damage occurs from Owner's alternate arrangements for Winterization or lack thereof, and it is further AGREED that the Condo may revoke this service at any time provided that the Owner is given due notice of discontinuation of said courtesy service by email or fax or US mail or via a phone, and it is further
                            <br />
                            <br />
                            AGREED that Condo will take every reasonable care to perform said Winterization including but not limited to service with care and to standards reasonably expected to prevent freeze/frost damage, however, should damage occur to Owner's unit during and/or after Winterization from any conditions including but not limited to, weather conditions such as, but not limited to, prolonged or unseasonable cold temperatures, etc. Owners as Indemnitors shall Indemnify and hold harmless Condo, as Indemnitees, its shareholders, officers, agents and/or management company Dependable Management Services, Inc., from all loss, damages, costs and attorney's fees that may result from said weather damages, and it is further AGREED that Owners shall have no claim or cause of action against property management company Dependable Management Services, of P.O. Box 416, Monroe, NY 10949, for any loss, damage or costs arising due to prolonged and/or unseasonably cold weather on the Owners'
                            respective unit, or for that matter any other loss, damage or costs arising as a part of the winterization process, and it is further
                            <br />
                            <br />
                            AGREED that the terms of this service shall terminate with the sale of the Unit. The undersigned hereby agree to the terms until such time as this agreement is revoked by Notice, as described above: The undersigned understands and acknowledges that, by clicking 'Accept' it is consenting and agreeing to the Condominium's engaging in the Winterization service for their unit pursuant to this contract, and the undersigned understands and acknowledges that he/she will be bound by said contract by clicking 'Accept'.
                        </p>
                        <div className="select-none">
                            <label htmlFor={`AcceptedAgreement`}>
                                <input id={`AcceptedAgreement`} className="checked:bg-dependable-gold w-0 h-0" type="checkbox" onChange={(e) => setAcceptedAgreement(e.target.checked)} checked={AcceptedAgreement} />
                                <span className={` w-5 h-5 inline-flex items-center justify-center align-text-bottom mr-2 md:mr-3 border border-dependable-gold ${AcceptedAgreement ? "bg-dependable-gold" : "bg-white"}`}>{!!AcceptedAgreement && <FaCheck className="text-white" />}</span>I agree to the terms and conditions
                            </label>
                        </div>
                    </div> */}
                </div>
                <div className="grid grid-cols-12 md:gap-8 mb-8">
                    <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold"></div>
                    <div className="md:col-span-5 col-span-12">
                        <button type="submit" disabled={Requesting || SubmitSuccess} className="disabled:opacity-50 border-2 border-dependable-green py-3 px-5 font-semibold inline-block">
                            {Requesting ?
                                'One Moment...' : SubmitSuccess ?
                                    'Submitted Successfully' : 'Submit Request'}
                            <FaArrowAltCircleRight className="inline-block text-dependable-green ml-3 align-text-top" />
                        </button>
                    </div>
                </div>
                {!!Error && (
                    <div className="grid grid-cols-12 md:gap-8 mb-8">
                        <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold"></div>
                        <div className="md:col-span-5 col-span-12">
                            <p className="text-red-700">{Error}</p>
                        </div>
                    </div>
                )}
                {SubmitSuccess && (
                    <div className="grid grid-cols-12 md:gap-8 mb-8">
                        <div className="md:col-span-2 md:col-end-4 col-span-12 self-center font-semibold"></div>
                        <div className="md:col-span-8 col-span-12">
                            <p className="text-dependable-green font-extrabold">
                                Thank you for your request. Please allow 2-3 business days for your request to be confirmed.
                                <br />
                                If you do not receive a confirmation email after 3 business days, please contact us at winter@dependablems.com to confirm receipt of your request
                                <br />
                                Requests submitted after Wednesday 3:00pm will be processed the following week
                            </p>
                        </div>
                    </div>
                )}
            </form>
            {showConfirmationModal
                && <BypassModal title="Confirm Dewinterization Request" content={<p>You have reached the maximum number of winterization requests. After you de-winterize, we will not be able to re-winterize this unit again.<br />
                    <b>Would you like to proceed?</b></p>} showVerifyCode={false} actions={[{
                        text: "Cancel",
                        onClick: () => setShowConfirmationModal(false), className: "text-md md:text-lg border-4 border-gray-400 text-gray-700 px-6 py-4 transition duration-300 hover:text-white hover:bg-gray-400/80"
                    }, {
                        text: "Continue Anyway",
                        onClick: () => {
                            setShowConfirmationModal(false);
                            submitRequest(null, false, true);
                        }, className: "text-md md:text-lg border-4 border-dependable-green px-6 py-4 transition duration-300 inline-block hover:bg-dependable-green/80 hover:text-white"
                    }]} isOpen={showConfirmationModal} onClose={() => setShowConfirmationModal(false)} />}
            <RewinterizationModal isOpen={showRewinterizationModal} onClose={() => setShowRewinterizationModal(false)} />
        </>
    );
};

export default DewinterForm; // minus 140 lines
